import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useUserContext } from 'talent_matching/contexts/user_context';
import AppliedFilterChip, {
  AppliedFilterChipProps,
} from 'talent_matching/components/applied_filters/applied_filter_chip';
import { SCORECARD_STATUS } from 'talent_search/shared/types';
import { AppliedOnType, CompanyNameFilter } from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import AppliedAdvancedFilterChip from 'talent_matching/components/applied_filters/applied_advanced_filter_chip';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { formatShortDate } from 'talent_search/lib/utils';
import BaseAppliedFiltersRow from 'talent_matching/components/shared/base_applied_filters_row';
import { FORM_DEFAULTS } from 'talent_matching/components/filter_form';
import {
  APPLIED_ON_TEXT_MAPPING,
  isAppliedOnFiltered,
} from 'talent_matching/applied_on';

const t = withLocalePrefix('talent_matching.filters.applied');

const AppliedFiltersRow = () => {
  const { getValues } = useFormContext();
  const { canUseLocations, userDateFormat } = useUserContext();

  const [
    advancedFilters,
    appliedOnEnd,
    appliedOnStart,
    appliedOnType,
    jobPosts,
    excludePreviouslyRejected,
    feedbackStatus,
    locationFilterType,
    debouncedLocationName,
    geolocation,
    geolocationRadius,
    manualReview,
    priorityCandidates,
    savedForLater,
    excludeHighRisk,
    otherActiveApplications,
    minExperience,
    maxExperience,
    companyNameFilters,
    canHave,
    mustHave,
    doesNotHave,
    questionFilters,
    educationFilters,
  ] = getValues([
    'advancedFilters',
    'appliedOnEnd',
    'appliedOnStart',
    'appliedOnType',
    'jobPosts',
    'excludePreviouslyRejected',
    'feedbackStatus',
    'locationFilterType',
    'debouncedLocationName',
    'geolocation',
    'geolocationRadius',
    'manualReview',
    'priorityCandidates',
    'savedForLater',
    'excludeHighRisk',
    'otherActiveApplications',
    'minExperience',
    'maxExperience',
    'companyNameFilters',
    'canHave',
    'mustHave',
    'doesNotHave',
    'questionFilters',
    'educationFilters',
  ]);

  const buildAppliedFilters = (): Omit<
    AppliedFilterChipProps,
    'formDefaults'
  >[] => {
    const filters: Omit<AppliedFilterChipProps, 'formDefaults'>[] = [];

    if (priorityCandidates) {
      filters.push({
        label: t('priority_candidates'),
        filterKeys: ['priorityCandidates'],
        uniqueKey: t('priority_candidates'),
      });
    }

    if (manualReview) {
      filters.push({
        label: t('manual_review'),
        filterKeys: ['manualReview'],
        uniqueKey: t('manual_review'),
      });
    }

    if (savedForLater) {
      filters.push({
        label: t('saved_for_later'),
        filterKeys: ['savedForLater'],
        uniqueKey: t('saved_for_later'),
      });
    }

    if (feedbackStatus !== SCORECARD_STATUS.ALL) {
      filters.push({
        label: t(`feedback_status_${feedbackStatus}`),
        filterKeys: ['feedbackStatus'],
        uniqueKey: t(`feedback_status_${feedbackStatus}`),
      });
    }

    if (excludeHighRisk) {
      filters.push({
        label: t('exclude_high_risk'),
        filterKeys: ['excludeHighRisk'],
        uniqueKey: t('exclude_high_risk'),
      });
    }

    if (otherActiveApplications) {
      filters.push({
        label: t(
          otherActiveApplications === '1'
            ? 'other_active_applications_include'
            : 'other_active_applications_exclude'
        ),
        filterKeys: ['otherActiveApplications'],
        uniqueKey:
          otherActiveApplications === '1'
            ? t('other_active_applications_include')
            : t('other_active_applications_exclude'),
      });
    }

    if (isAppliedOnFiltered({ appliedOnType, appliedOnStart, appliedOnEnd })) {
      const startDateText =
        formatShortDate(appliedOnStart, userDateFormat) || '...';
      const endDateText =
        formatShortDate(appliedOnEnd, userDateFormat) || '...';
      const whenText = APPLIED_ON_TEXT_MAPPING[appliedOnType as AppliedOnType](
        startDateText,
        endDateText
      );

      filters.push({
        label: t('applied_on', { when: whenText }),
        filterKeys: ['appliedOnType', 'appliedOnStart', 'appliedOnEnd'],
        uniqueKey: 'applied_on',
      });
    }

    if (jobPosts.length > 0) {
      const jobPostNames = jobPosts
        .map((item: Item) => item.label)
        .filter(Boolean)
        .join(', ');

      filters.push({
        label: t('job_post_name', { job_posts: jobPostNames }),
        filterKeys: ['jobPosts'],
        uniqueKey: 'job_post_name',
      });
    }

    if (canUseLocations) {
      if (locationFilterType === 'radius' && geolocation) {
        const locationText = t('location', {
          radius: geolocationRadius,
          location: geolocation.city,
        });
        filters.push({
          label: t('located', { location: locationText }),
          filterKeys: ['geolocation', 'geolocationRadius'],
          uniqueKey: 'located',
        });
      }

      if (locationFilterType === 'name' && debouncedLocationName) {
        filters.push({
          label: t('located_in', { location: debouncedLocationName }),
          filterKeys: ['locationName'],
          uniqueKey: 'located_in',
        });
      }
    }

    if (excludePreviouslyRejected) {
      filters.push({
        label: t('exclude_previously_rejected'),
        filterKeys: ['excludePreviouslyRejected'],
        uniqueKey: t('exclude_previously_rejected'),
      });
    }

    if (minExperience) {
      const yearsText = t('years', { count: Number(minExperience) });
      filters.push({
        label: t('minimum_experience', { years: yearsText }),
        filterKeys: ['minExperience'],
        uniqueKey: 'minimum_experience',
      });
    }

    if (maxExperience) {
      const yearsText = t('years', { count: Number(maxExperience) });
      filters.push({
        label: t('maximum_experience', { years: yearsText }),
        filterKeys: ['maxExperience'],
        uniqueKey: 'maximum_experience',
      });
    }

    if (companyNameFilters.length > 0) {
      companyNameFilters.forEach((filter: CompanyNameFilter) => {
        if (filter.value) {
          filters.push({
            label: t(`company_name_${filter.mode}`, { company: filter.value }),
            filterKeys: [`companyNameFilters.${filter.id}`],
            uniqueKey: `companyNameFilters.${filter.id}`,
          });
        }
      });
    }

    if (canHave.length > 0) {
      filters.push({
        label: t('keywords.can_have', { keywords: canHave.join(', ') }),
        filterKeys: ['canHave'],
        uniqueKey: 'keywords.can_have',
      });
    }

    if (mustHave.length > 0) {
      filters.push({
        label: t('keywords.must_have', { keywords: mustHave.join(', ') }),
        filterKeys: ['mustHave'],
        uniqueKey: 'keywords.must_have',
      });
    }

    if (doesNotHave.length > 0) {
      filters.push({
        label: t('keywords.does_not_have', {
          keywords: doesNotHave.join(', '),
        }),
        filterKeys: ['doesNotHave'],
        uniqueKey: 'keywords.does_not_have',
      });
    }

    return filters;
  };

  const buildAdvancedFilters = (): AdvancedFilter[] => {
    const filters: AdvancedFilter[] = [];
    advancedFilters.forEach((filter: AdvancedFilter) => {
      if (filter.queryKey() && filter.queryParamValue()) {
        filters.push(filter);
      }
    });
    return filters;
  };

  const buildQuestionFilters = (): AdvancedFilter[] => {
    const filters: AdvancedFilter[] = [];
    questionFilters.forEach((filter: AdvancedFilter) => {
      if (filter.queryKey() && filter.queryParamValue()) {
        filters.push(filter);
      }
    });

    return filters;
  };

  const buildEducationFilters = (): AdvancedFilter[] => {
    const filters: AdvancedFilter[] = [];
    educationFilters.forEach((filter: AdvancedFilter) => {
      if (filter.queryKey() && filter.queryParamValue()) {
        filters.push(filter);
      }
    });

    return filters;
  };

  const appliedFilters = buildAppliedFilters();
  const appliedAdvancedFilters = buildAdvancedFilters();
  const appliedQuestionFilters = buildQuestionFilters();
  const appliedEducationFilters = buildEducationFilters();

  const chips: React.ReactNode[] = [
    ...appliedFilters.map((filter) => (
      <AppliedFilterChip
        key={filter.uniqueKey}
        {...filter}
        formDefaults={FORM_DEFAULTS as Record<string, unknown>}
      />
    )),
    ...appliedAdvancedFilters.map((filter) => (
      <AppliedAdvancedFilterChip
        filter={filter}
        key={filter.reactKey}
        fieldName="advancedFilters"
      />
    )),
    ...appliedQuestionFilters.map((filter) => (
      <AppliedAdvancedFilterChip
        filter={filter}
        key={filter.reactKey}
        fieldName="questionFilters"
      />
    )),
    ...appliedEducationFilters.map((filter) => (
      <AppliedAdvancedFilterChip
        filter={filter}
        key={filter.reactKey}
        fieldName="educationFilters"
      />
    )),
  ];

  return <BaseAppliedFiltersRow chips={chips} />;
};

export default AppliedFiltersRow;
